<template>
  <app-box
    :title="hideTitle ? '' : $t('notes-and-history')"
    class="app-analysis-notes-and-history"
    :class="{ 'app-analysis-notes-and-history--embedded': hideTitle }"
  >
    <div v-if="showWhatsappTab" class="app-analysis-notes-and-history-tabs">
      <button
        type="button"
        class="app-analysis-notes-and-history-tab"
        :class="{ 'is-active': activeTab === 'notes' }"
        @click="onSelectTab('notes')"
      >
        {{ $t("notes-and-history") }}
      </button>
      <button
        type="button"
        class="app-analysis-notes-and-history-tab"
        :class="{ 'is-active': activeTab === 'whatsapp' }"
        @click="onSelectTab('whatsapp')"
      >
        {{ $t("whatsapp-chat") }}
      </button>
    </div>

    <app-whatsapp-panel
      v-if="showWhatsappTab && activeTab === 'whatsapp'"
      ref="whatsappPanel"
      :analysis="analysis"
    />

    <div v-show="activeTab === 'notes'">
    <div class="app-analysis-notes-and-history-filter-row" :class="{ 'has-toolbar': hasToolbar }">
      <div v-if="hasToolbar" class="app-analysis-notes-and-history-toolbar">
        <slot name="toolbar" />
      </div>
      <div class="app-analysis-notes-and-history-type-filter">
        <button
          type="button"
          class="notes-subscription-btn"
          :class="{ 'is-subscribed': isSubscribed, 'is-mandatory': isSubscriptionMandatory }"
          :disabled="subscriptionLoading || (isSubscribed && isSubscriptionMandatory)"
          :title="subscriptionButtonTitle"
          @click="onToggleSubscription"
        >
          <app-icon :icon="isSubscribed ? 'bell-fill' : 'bell'" />
        </button>
        <button
          v-if="hasEvaluationMessages"
          type="button"
          class="notes-evaluation-messages-btn"
          :class="{ 'is-active': showEvaluationMessages }"
          :title="$t(showEvaluationMessages ? 'notes-hide-evaluation-messages' : 'notes-show-evaluation-messages').toString()"
          @click="onToggleEvaluationMessages"
        >
          <app-icon :icon="showEvaluationMessages ? 'bug-fill' : 'bug'" />
        </button>
        <app-multiselect
          v-if="hasEvalBatches"
          v-model="selectedEbNums"
          :options="ebOptions"
          :allowEmpty="true"
          :placeholder="$t('eb-filter')"
          small
        />
        <app-multiselect
          v-model="selectedNoteTypes"
          :options="noteTypeFilterOptions"
          :allowEmpty="true"
          :close-on-select="false"
          :placeholder="$t('filter-by-type')"
          small
        />
      </div>
    </div>

    <div
      ref="notesList"
      class="app-analysis-notes-and-history-list mar-bottom"
      :aria-busy="notesLoading ? 'true' : 'false'"
    >
      <template v-if="notesLoading">
        <div
          v-for="(skeleton, index) in noteLoadingSkeletons"
          :key="'skeleton-' + index"
          class="note-entry note-entry--skeleton"
          :class="skeleton.variant"
          aria-hidden="true"
        >
          <span class="note-skeleton-bar" :style="{ width: skeleton.width }" />
        </div>
      </template>
      <p v-else-if="notes.length === 0" class="text-muted">{{ $t("notes-and-history-empty") }}</p>
      <p v-else-if="filteredNotes.length === 0" class="text-muted">{{ $t("nothing-found") }}</p>

      <template v-else>
      <div
        v-for="note in filteredNotes"
        :key="note.id"
        class="note-entry"
        :data-note-id="note.id"
        :class="[
          'note-entry-' + note.action_type.toLowerCase(),
          {
            'note-entry--history': note.action_type !== 'NOTE',
            'note-entry--highlight': highlightedNoteId === note.id,
          },
        ]"
      >
        <div class="note-entry-head">
          <span class="note-entry-author" :class="{ 'is-system': !note.user }">
            {{ authorName(note) }}
          </span>
          <div class="note-entry-meta">
            <span v-if="note.eval_batch_num !== null" class="note-entry-eb-badge">
              EB{{ note.eval_batch_num }}
            </span>
            <span class="note-entry-type-label" :class="'type-' + note.action_type.toLowerCase()">
              {{ $t("analysis-note-type-" + note.action_type.toLowerCase()) }}
            </span>
            <span v-if="note.edited_at" class="note-entry-edited text-muted">{{ $t("note-edited") }}</span>
            <span class="note-entry-time text-muted">{{ formatDateTime(note.created_at) }}</span>
            <button
              type="button"
              class="note-entry-reply-btn"
              :class="{ 'is-active': replyingToNoteId === note.id }"
              :title="$t('reply').toString()"
              @click="onReplyToNote(note)"
            >
              <app-icon icon="reply" />
            </button>
            <button
              v-if="canEditNote(note)"
              type="button"
              class="note-entry-edit-btn"
              :class="{ 'is-active': editingNoteId === note.id }"
              :title="$t('edit').toString()"
              @click="onEditNote(note)"
            >
              <app-icon icon="pencil" />
            </button>
            <button
              v-if="canEditNote(note)"
              type="button"
              class="note-entry-delete-btn"
              :title="$t('delete').toString()"
              @click="onDeleteNote(note)"
            >
              <app-icon icon="trash" />
            </button>
          </div>
        </div>

        <button
          v-if="hasResolvableParent(note)"
          type="button"
          class="note-entry-reply-quote"
          @click="onScrollToParent(note)"
        >
          <span class="note-entry-reply-quote-author">{{ parentAuthorName(note) }}</span>
          <span class="note-entry-reply-quote-text">{{ parentReplyPreview(note) }}</span>
        </button>

        <app-evaluation-message v-if="isEvaluationMessage(note)" :message="note.description" />
        <div v-else class="note-entry-body" v-html="renderBody(note)"></div>

        <div v-if="note.attachments.length" class="note-entry-attachments">
          <template v-for="attachment in note.attachments">
            <app-image-lightbox
              v-if="isImage(attachment) && attachment.url"
              :key="attachment.id"
              :src="attachment.url"
              :download-filename="attachment.file_name"
            >
              <img
                :src="attachment.url"
                :alt="attachment.file_name"
                class="note-entry-attachment-image"
              />
            </app-image-lightbox>
            <a
              v-else
              :key="'file-' + attachment.id"
              :href="attachment.url || '#'"
              target="_blank"
              rel="noopener"
              class="note-entry-attachment"
              :title="attachment.file_name"
            >
              <span class="note-entry-attachment-file">
                <app-icon icon="paperclip" />
                <span class="note-entry-attachment-filename">{{ attachment.file_name }}</span>
              </span>
            </a>
          </template>
        </div>

        <div v-if="note.action_type === 'NOTE'" class="note-entry-reactions">
          <button
            v-for="reaction in groupedReactions(note)"
            :key="reaction.emoji"
            type="button"
            class="note-reaction-chip"
            :class="{ 'is-mine': reaction.reactedByMe }"
            :title="reaction.reactorNames.join(', ')"
            @click="onToggleReaction(note, reaction.emoji)"
          >
            <span class="note-reaction-emoji">{{ reaction.emoji }}</span>
            <span class="note-reaction-count">{{ reaction.count }}</span>
          </button>

          <div class="note-reaction-add" @click.stop>
            <button
              type="button"
              class="note-reaction-add-btn"
              :title="$t('add-reaction').toString()"
              @click="onToggleReactionPicker(note.id)"
            >
              <app-icon icon="emoji-smile" />
            </button>
            <div v-if="openReactionPickerNoteId === note.id" class="note-reaction-picker">
              <button
                v-for="emoji in reactionEmojis"
                :key="emoji"
                type="button"
                class="note-reaction-picker-option"
                @click="onSelectReaction(note, emoji)"
              >
                {{ emoji }}
              </button>
            </div>
          </div>
        </div>
      </div>
      </template>
    </div>

    <b-form-group :label="$t('note')" class="app-analysis-notes-and-history-composer">
      <div v-if="editingNoteId" class="composer-editing-banner">
        <span>{{ $t("note-editing") }}</span>
        <button type="button" class="composer-editing-cancel" @click="onCancelNoteEdit">
          {{ $t("cancel") }}
        </button>
      </div>
      <div v-else-if="replyingToNote" class="composer-replying-banner">
        <button type="button" class="composer-replying-preview" @click="onScrollToNote(replyingToNote.id)">
          <span>{{ $t("note-replying", { author: authorName(replyingToNote) }) }}</span>
          <span class="composer-replying-preview-text">{{ replyPreview(replyingToNote) }}</span>
        </button>
        <button type="button" class="composer-editing-cancel" @click="onCancelReply">
          {{ $t("cancel") }}
        </button>
      </div>

      <b-textarea
        ref="noteTextarea"
        v-model="note"
        rows="2"
        :placeholder="$t('note-composer-placeholder')"
        @input="onNoteInput"
        @keydown.native="onNoteKeydown"
        @paste.native="onPasteIntoNote"
      />

      <div v-if="mentionSuggestions.length" class="app-analysis-notes-and-history-mentions">
        <div
          v-for="(user, index) in mentionSuggestions"
          :key="user.id"
          class="mention-suggestion"
          :class="{ 'is-active': index === mentionActiveIndex }"
          @mousedown.prevent="onSelectMention(user)"
          @mouseenter="onMentionSuggestionHover(index)"
        >
          {{ fullName(user) }} <small class="text-muted">{{ user.email }}</small>
        </div>
      </div>

      <!-- Attached files preview (compact chips) -->
      <div v-if="hasComposerAttachments" class="attached-files-preview">
        <div
          v-for="attachment in editingExistingAttachments"
          :key="'existing-' + attachment.id"
          class="attached-file-chip"
        >
          <app-icon :icon="isImage(attachment) ? 'image' : 'paperclip'" />
          <span class="file-name" :title="attachment.file_name">{{ attachment.file_name }}</span>
          <button
            type="button"
            class="btn-remove-file"
            @click="removeEditingExistingAttachment(attachment.id)"
            :title="$t('remove').toString()"
          >
            <app-icon icon="x" />
          </button>
        </div>
        <div v-for="(file, index) in attachmentFilesArray" :key="'new-' + index" class="attached-file-chip">
          <app-icon :icon="isImageFile(file) ? 'image' : 'paperclip'" />
          <span class="file-name" :title="file.name">{{ file.name }}</span>
          <button type="button" class="btn-remove-file" @click="removeAttachedFile(index)" :title="$t('remove').toString()">
            <app-icon icon="x" />
          </button>
        </div>
      </div>

      <div class="composer-footer">
        <div class="composer-actions-left">
          <button type="button" class="btn-attach" @click="triggerFileInput" :title="$t('attach-files').toString()">
            <app-icon icon="plus" />
          </button>
          <input
            type="file"
            ref="fileInput"
            style="display: none"
            multiple
            accept="*"
            @change="onFileSelected"
          />
          <app-multiselect
            v-if="hasEvalBatches"
            v-model="selectedEbNum"
            :options="ebOptions"
            :singleSelect="true"
            :allowEmpty="true"
            :placeholder="$t('which-eb')"
            small
          />
        </div>
        <div class="composer-actions-right">
          <app-button type="button" :loading="loading" @click="onComposerSubmit" :disabled="isAppendNoteButtonDisabled">
            {{ editingNoteId ? $t("save") : $t("send") }}
          </app-button>
          <small class="composer-send-hint text-muted">{{ $t("note-composer-send-hint") }}</small>
        </div>
      </div>
    </b-form-group>
    </div>
  </app-box>
</template>

<script lang="ts">
import { Component, Prop, Ref, Watch } from "vue-property-decorator";
import AppButton from "@/app/shared/components/app-button/app-button.vue";
import AppBox from "@/app/shared/components/app-box/app-box.vue";
import AppEvaluationMessage from "@/app/shared/components/app-evaluation-message/app-evaluation-message.vue";
import AppIcon from "@/app/shared/components/app-icon/app-icon.vue";
import AppImageLightbox from "@/app/shared/components/app-image-lightbox/app-image-lightbox.vue";
import AppWhatsappPanel from "@/app/analysis/edit-analysis/edit-analysis-admin/whatsapp-panel.vue";
import AppMultiselect from "@/app/shared/components/app-multiselect/app-multiselect.vue";
import { MultiselectOption } from "@/app/shared/components/app-multiselect/types";
import { AnalysisSchema } from "@/app/shared/services/volateq-api/api-schemas/analysis-schema";
import {
  ANALYSIS_NOTE_ACTION_TYPES,
  AnalysisNoteActionType,
  AnalysisNoteSchema,
  AnalysisNoteAttachmentSchema,
  AnalysisNoteSubscriptionStatus,
} from "@/app/shared/services/volateq-api/api-schemas/analysis-note-schema";
import { SimpleUserRoleSchema } from "@/app/shared/services/volateq-api/api-schemas/user-schemas";
import { CatchError } from "@/app/shared/services/helper/catch-helper";
import {
  AnalysisNotesHelper,
  GroupedReaction,
  NoteMention,
  REACTION_EMOJIS,
} from "@/app/shared/services/helper/analysis-notes-helper";
import type { UnsavedComposerDraft } from "@/app/shared/services/helper/analysis-notes-helper";
import { DomHelper } from "@/app/shared/services/helper/dom-helper";
import { appLocalStorage } from "@/app/shared/services/app-storage/app-storage";
import { analysisEventService } from "../../shared/analysis-event-service";
import { AnalysisEvent } from "../../shared/types";
import { BaseAuthOnlineComponent } from "@/app/shared/components/base-auth-component/base-auth-online-component";
import { userService } from "@/app/shared/services/user-service/user-service";
import { notesModalService } from "@/app/shared/services/notes-modal-service";
import { AnalysisNoteChannel } from "@/app/shared/services/realtime/channels/analysis-note-channel";

interface IAppTextarea {
  $el: HTMLTextAreaElement;
  focus(): void;
}

interface IAppWhatsappPanel {
  hasUnsavedComposerContent: boolean;
}

const NOTE_TYPE_FILTER_ORDER: AnalysisNoteActionType[] = [
  "NOTE",
  "STATE_UPDATE",
  "RELEASE_LOCK",
  "CHANGED_FLOWN_AT",
  "TASK",
  "ROLE_ASSIGNED",
  "READY_FOR_REVIEW",
  "DEADLINE_CHANGED",
  "CHANGED_PRODUCT_PACKAGES",
  "CHANGED_DRONE",
  "CHANGED_KEY_FIGURES",
];

const SHOW_EVALUATION_MESSAGES_STORAGE_KEY = "notes-show-evaluation-messages";

interface NoteLoadingSkeleton {
  variant: "note-entry-note" | "note-entry--history";
  width: string;
}

/** Static chat-shaped placeholders shown while the notes thread is fetching. */
const NOTE_LOADING_SKELETONS: NoteLoadingSkeleton[] = [
  { variant: "note-entry--history", width: "58%" },
  { variant: "note-entry-note", width: "78%" },
  { variant: "note-entry--history", width: "46%" },
  { variant: "note-entry-note", width: "86%" },
  { variant: "note-entry--history", width: "52%" },
];

@Component({
  name: "app-analysis-notes-and-history",
  components: {
    AppButton,
    AppBox,
    AppEvaluationMessage,
    AppIcon,
    AppImageLightbox,
    AppMultiselect,
    AppWhatsappPanel,
  },
})
export default class AppAnalysisNotesAndHistory extends BaseAuthOnlineComponent {
  @Prop({ required: true }) analysis!: AnalysisSchema;
  @Prop({ type: Boolean, default: false }) hideTitle!: boolean;

  activeTab: "notes" | "whatsapp" = "notes";
  @Ref() noteTextarea!: IAppTextarea;
  @Ref() notesList!: HTMLDivElement;
  @Ref() fileInput!: HTMLInputElement;
  @Ref() whatsappPanel!: IAppWhatsappPanel;

  notes: AnalysisNoteSchema[] = [];
  selectedNoteTypes: AnalysisNoteActionType[] = [];
  selectedEbNums: number[] = [];
  selectedEbNum: number | null = null;
  showEvaluationMessages = appLocalStorage.getItem<boolean>(SHOW_EVALUATION_MESSAGES_STORAGE_KEY) ?? true;
  note: string | null = null;
  notesLoading = true;
  loading = false;

  readonly noteLoadingSkeletons = NOTE_LOADING_SKELETONS;

  // Id of the note currently being edited in the composer, or null when composing a new note.
  editingNoteId: string | null = null;
  /** Attachments already on the note being edited, shown in the composer until save or cancel. */
  editingExistingAttachments: AnalysisNoteAttachmentSchema[] = [];
  private editingInitialAttachmentIds: string[] = [];

  /** Id of the note the composer is currently replying to, or null when not in reply mode. */
  replyingToNoteId: string | null = null;
  highlightedNoteId: string | null = null;
  private highlightTimeout: number | null = null;

  volateqUsers: SimpleUserRoleSchema[] = [];
  mentions: NoteMention[] = [];
  mentionSuggestions: SimpleUserRoleSchema[] = [];
  mentionActiveIndex = -1;
  mentionStart = -1;
  mentionQuery = "";

  attachmentFiles: File | File[] | null = null;
  uploadProgress = null;
  currentUserId: string | null = null;
  openReactionPickerNoteId: string | null = null;
  private scrollToBottomCleanup: (() => void) | null = null;
  /** Remaining filter-watcher scroll-to-bottom skips (used by scroll-to-parent when clearing filters). */
  private filterScrollSuppressCount = 0;

  isSubscribed = false;
  isSubscriptionMandatory = false;
  subscriptionLoading = false;

  private noteChannel: AnalysisNoteChannel | null = null;
  private noteChannelUnsubscribe: (() => void) | null = null;
  /** Signature of the rendered thread, so an auto refresh that brought nothing is a no-op. */
  private renderedThreadFingerprint = "";
  private refreshingThread = false;
  /** Set by an event that arrived while a refresh was still running, so none is swallowed. */
  private threadRefreshPending = false;

  beforeDestroy() {
    this.scrollToBottomCleanup?.();
    this.scrollToBottomCleanup = null;
    this.clearHighlightTimeout();
    this.closeReactionPicker();
    this.stopThreadAutoRefresh();
  }

  get replyingToNote(): AnalysisNoteSchema | null {
    return AnalysisNotesHelper.findNoteById(this.notes, this.replyingToNoteId);
  }

  get reactionEmojis(): string[] {
    return REACTION_EMOJIS;
  }

  get showWhatsappTab(): boolean {
    return this.hasPerm(this.p.READ_ANALYSIS_HISTORY);
  }

  /** Leaving the WhatsApp tab unmounts its panel, so an unsent message would be lost. */
  @CatchError()
  onSelectTab(tab: "notes" | "whatsapp") {
    if (tab !== this.activeTab && this.hasUnsavedWhatsappMessage) {
      if (!confirm(this.$t("whatsapp-discard-confirm").toString())) {
        return;
      }
    }

    this.activeTab = tab;
  }

  @CatchError()
  async created() {
    analysisEventService.on(this.analysis.id, AnalysisEvent.UPDATE_ANALYSIS, async () => {
      await Promise.all([this.updateNotes(), this.loadSubscriptionStatus()]);
    });

    this.notesLoading = true;
    try {
      const [, me] = await Promise.all([
        this.loadVolateqUsers(),
        userService.me(),
        this.updateNotes(),
        this.loadSubscriptionStatus(),
      ]);
      this.currentUserId = me.id;
    } finally {
      this.notesLoading = false;
    }

    // Scroll only after skeletons are gone and real notes are in the DOM.
    await this.$nextTick();
    this.scrollNotesListToBottom();
    await this.markNotesRead();

    this.startThreadAutoRefresh();
  }

  /**
   * Keeps the open thread live: whenever anybody appends, edits, deletes or reacts to an entry -
   * or the system logs one - the thread refetches itself, so it never shows a stale conversation
   * while it is on screen.
   */
  private startThreadAutoRefresh() {
    this.noteChannel = new AnalysisNoteChannel();
    this.noteChannelUnsubscribe = this.noteChannel.subscribe(() => void this.refreshThread());
    void this.noteChannel.join(this.analysis.id);

    // A hidden tab gets its timers throttled and its socket suspended, so catch up on
    // everything that happened while it was in the background as soon as it is shown again.
    document.addEventListener("visibilitychange", this.onVisibilityChanged);
  }

  private stopThreadAutoRefresh() {
    document.removeEventListener("visibilitychange", this.onVisibilityChanged);

    this.noteChannelUnsubscribe?.();
    this.noteChannelUnsubscribe = null;
    void this.noteChannel?.destroy();
    this.noteChannel = null;
  }

  private onVisibilityChanged = () => {
    if (document.visibilityState === "visible") {
      void this.refreshThread();
    }
  };

  /**
   * Refetches the thread after somebody else changed it. It runs while the user is working in
   * the thread, so it must not disturb them: the composer keeps its content, and the list only
   * follows the newest entry when it was already scrolled to the bottom.
   */
  private async refreshThread(): Promise<void> {
    this.threadRefreshPending = true;
    if (this.refreshingThread || this.notesLoading) {
      return;
    }

    this.refreshingThread = true;
    try {
      // A change committed while the running fetch was already in flight is not in its
      // response, so fetch once more instead of dropping the event that announced it.
      while (this.threadRefreshPending) {
        this.threadRefreshPending = false;
        await this.fetchThreadIntoView();
      }
    } finally {
      this.refreshingThread = false;
    }
  }

  private async fetchThreadIntoView(): Promise<void> {
    try {
      const fetchedNotes = await this.volateqApi.getAnalysisNotes(this.analysis.id);
      const wasScrolledToBottom = !this.notesList || DomHelper.isScrolledToBottom(this.notesList);

      if (!this.applyFetchedNotes(fetchedNotes)) {
        return;
      }

      // A note tagging the current user subscribes them to the thread on the backend.
      await this.loadSubscriptionStatus();

      if (wasScrolledToBottom) {
        await this.$nextTick();
        this.scrollNotesListToBottom();
      }

      await this.markNotesReadIfVisible();
    } catch (e) {
      // The next event (or fallback tick) retries; a failed refresh must not interrupt the user.
      console.error("Failed to refresh the analysis notes thread:", e);
    }
  }

  /** Entries arriving into the open thread only count as read while the user can actually see them. */
  private async markNotesReadIfVisible(): Promise<void> {
    if (document.visibilityState !== "visible" || this.activeTab !== "notes") {
      return;
    }

    await this.markNotesRead();
  }

  get subscriptionButtonTitle(): string {
    if (this.isSubscriptionMandatory) {
      return this.$t("notes-subscription-mandatory").toString();
    }
    if (this.isSubscribed) {
      return this.$t("notes-unsubscribe").toString();
    }
    return this.$t("notes-subscribe").toString();
  }

  private applySubscriptionStatus(status: AnalysisNoteSubscriptionStatus) {
    this.isSubscribed = status.subscribed;
    this.isSubscriptionMandatory = status.mandatory;
  }

  private async loadSubscriptionStatus() {
    try {
      this.applySubscriptionStatus(await this.volateqApi.getAnalysisNotesSubscription(this.analysis.id));
    } catch {
      // Subscription status is optional UI chrome; failing must not break the thread.
      this.isSubscribed = false;
      this.isSubscriptionMandatory = false;
    }
  }

  @CatchError("subscriptionLoading")
  async onToggleSubscription() {
    if (this.isSubscribed && this.isSubscriptionMandatory) {
      return;
    }

    if (this.isSubscribed) {
      this.applySubscriptionStatus(await this.volateqApi.unsubscribeAnalysisNotes(this.analysis.id));
    } else {
      this.applySubscriptionStatus(await this.volateqApi.subscribeAnalysisNotes(this.analysis.id));
    }
  }

  private async markNotesRead() {
    try {
      await this.volateqApi.markAnalysisNotesRead(this.analysis.id);
      notesModalService.notifyRead(this.analysis.id);
    } catch {
      // Read tracking only drives the unread highlight; failing to mark read must not break the thread.
    }
  }

  get isAppendNoteButtonDisabled(): boolean {
    const hasText = this.note !== null && this.note.trim() !== "";
    return !hasText && !this.hasComposerAttachments;
  }

  /**
   * Which composer holds unsent content, or null when closing the thread would lose nothing.
   * Consumers that can close the thread (e.g. the notes modal) use this to ask for confirmation.
   */
  get unsavedDraft(): UnsavedComposerDraft | null {
    if (this.hasUnsavedWhatsappMessage) {
      return "whatsapp";
    }
    if (this.hasUnsavedNote) {
      return "note";
    }
    return null;
  }

  private get hasUnsavedNote(): boolean {
    return AnalysisNotesHelper.hasUnsavedComposerContent({
      text: this.note,
      newFileCount: this.attachmentFilesArray.length,
      editedNote: AnalysisNotesHelper.findNoteById(this.notes, this.editingNoteId),
      keptExistingAttachmentIds: this.editingExistingAttachments.map(attachment => attachment.id),
    });
  }

  /** Only readable while the WhatsApp tab is open — the panel is unmounted on tab switch. */
  private get hasUnsavedWhatsappMessage(): boolean {
    return this.activeTab === "whatsapp" && Boolean(this.whatsappPanel?.hasUnsavedComposerContent);
  }

  get hasComposerAttachments(): boolean {
    return this.attachmentFilesArray.length > 0 || this.editingExistingAttachments.length > 0;
  }

  get attachmentFilesArray(): File[] {
    if (!this.attachmentFiles) {
      return [];
    }
    return Array.isArray(this.attachmentFiles) ? this.attachmentFiles : [this.attachmentFiles];
  }

  get noteTypeFilterOptions(): MultiselectOption<AnalysisNoteActionType>[] {
    return ANALYSIS_NOTE_ACTION_TYPES.map(actionType => ({
      id: actionType,
      label: this.$t(`analysis-note-type-${actionType.toLowerCase()}`).toString(),
    }));
  }

  get evalBatchNums(): number[] {
    return AnalysisNotesHelper.evalBatchNums(this.analysis.plant?.evaluation_batches || []);
  }

  get hasEvalBatches(): boolean {
    return this.evalBatchNums.length > 0;
  }

  get ebOptions(): MultiselectOption<number>[] {
    return this.evalBatchNums.map(num => ({ id: num, label: `EB${num}` }));
  }

  get hasToolbar(): boolean {
    return Boolean(this.$slots.toolbar);
  }

  get hasEvaluationMessages(): boolean {
    return this.notes.some(note => AnalysisNotesHelper.isEvaluationMessage(note));
  }

  get filteredNotes(): AnalysisNoteSchema[] {
    const notes = this.showEvaluationMessages
      ? this.notes
      : this.notes.filter(note => !AnalysisNotesHelper.isEvaluationMessage(note));
    return AnalysisNotesHelper.filterNotes(notes, this.selectedNoteTypes, this.selectedEbNums);
  }

  @CatchError()
  onToggleEvaluationMessages() {
    this.showEvaluationMessages = !this.showEvaluationMessages;
    appLocalStorage.setItem(SHOW_EVALUATION_MESSAGES_STORAGE_KEY, this.showEvaluationMessages);
  }

  isEvaluationMessage(note: AnalysisNoteSchema): boolean {
    return AnalysisNotesHelper.isEvaluationMessage(note);
  }

  @Watch("selectedNoteTypes")
  @Watch("selectedEbNums")
  async onFiltersChanged() {
    await this.$nextTick();
    if (this.filterScrollSuppressCount > 0) {
      this.filterScrollSuppressCount -= 1;
      return;
    }
    this.scrollNotesListToBottom();
  }

  onNoteInput(value: string) {
    const text = value || "";
    const cursorPosition = this.noteTextarea?.$el ? this.noteTextarea.$el.selectionStart : text.length;
    const mention = AnalysisNotesHelper.getMentionQuery(text, cursorPosition);

    if (mention) {
      this.mentionStart = mention.start;
      this.mentionQuery = mention.query;
      this.mentionSuggestions = AnalysisNotesHelper.filterUsers(this.volateqUsers, mention.query);
      this.mentionActiveIndex = this.mentionSuggestions.length > 0 ? 0 : -1;
    } else {
      this.onCloseMentions();
    }
  }

  @CatchError()
  onNoteKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      if (!this.isAppendNoteButtonDisabled && !this.loading) {
        void this.onComposerSubmit();
      }
      return;
    }

    if (event.key === "Escape") {
      if (this.mentionSuggestions.length > 0) {
        this.onCloseMentions();
      }
      return;
    }

    if (!this.mentionSuggestions.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.mentionActiveIndex = (this.mentionActiveIndex + 1) % this.mentionSuggestions.length;
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.mentionActiveIndex =
        (this.mentionActiveIndex - 1 + this.mentionSuggestions.length) % this.mentionSuggestions.length;
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const user = this.mentionSuggestions[this.mentionActiveIndex];
      if (user) {
        this.onSelectMention(user);
      }
    }
  }

  onMentionSuggestionHover(index: number) {
    this.mentionActiveIndex = index;
  }

  @CatchError()
  onPasteIntoNote(event: ClipboardEvent) {
    const images = AnalysisNotesHelper.extractImageFilesFromClipboard(event);
    if (images.length === 0) {
      return;
    }

    // Pasted a screenshot/image: attach it instead of inserting raw clipboard content.
    event.preventDefault();
    this.attachmentFiles = [...this.attachmentFilesArray, ...images];
  }

  @CatchError()
  async onSelectMention(user: SimpleUserRoleSchema) {
    const token = AnalysisNotesHelper.buildMentionToken(user);
    this.note = AnalysisNotesHelper.insertMention(this.note || "", this.mentionStart, this.mentionQuery.length, token);
    this.mentions.push({ token, userId: user.id });
    this.onCloseMentions();

    await this.$nextTick();
    this.noteTextarea?.focus();
  }

  onCloseMentions() {
    this.mentionSuggestions = [];
    this.mentionActiveIndex = -1;
    this.mentionQuery = "";
    this.mentionStart = -1;
  }

  @CatchError("loading")
  async onComposerSubmit() {
    if (this.editingNoteId) {
      await this.saveEditedNote(this.editingNoteId);
    } else {
      await this.appendNote();
    }
  }

  private async appendNote() {
    const noteText = this.note && this.note.trim() !== "" ? this.note : "";
    const mentionedUserIds = AnalysisNotesHelper.resolveMentionedUserIds(noteText, this.mentions);
    const createdNote = await this.volateqApi.addAnalysisNote(
      this.analysis.id,
      noteText,
      mentionedUserIds,
      this.selectedEbNum,
      this.replyingToNoteId,
    );

    const files = this.attachmentFilesArray;
    if (files.length > 0) {
      await this.volateqApi.uploadAnalysisNoteAttachments(this.analysis.id, createdNote.id, files);
    }

    this.resetComposer();

    // The user just posted: show their own note, wherever they were scrolled to.
    await this.updateNotes(true);
    // Authoring a note auto-subscribes the current user on the backend.
    await this.loadSubscriptionStatus();
    analysisEventService.emit(this.analysis.id, AnalysisEvent.UPDATE_ANALYSIS);
  }

  private async saveEditedNote(noteId: string) {
    const noteText = this.note && this.note.trim() !== "" ? this.note : "";
    // Re-resolve mentions from the edited text so a newly typed @-mention is tagged too. The
    // backend only notifies users tagged for the first time, never those already mentioned.
    const mentionedUserIds = AnalysisNotesHelper.resolveMentionedUserIdsFromText(noteText, this.volateqUsers);

    const removedAttachmentIds = this.editingInitialAttachmentIds.filter(
      attachmentId => !this.editingExistingAttachments.some(attachment => attachment.id === attachmentId),
    );

    for (const attachmentId of removedAttachmentIds) {
      await this.volateqApi.deleteAnalysisNoteAttachment(this.analysis.id, noteId, attachmentId);
    }

    await this.volateqApi.updateAnalysisNote(
      this.analysis.id,
      noteId,
      noteText,
      mentionedUserIds,
      this.selectedEbNum,
    );

    const files = this.attachmentFilesArray;
    if (files.length > 0) {
      await this.volateqApi.uploadAnalysisNoteAttachments(this.analysis.id, noteId, files);
    }

    this.resetComposer();

    // Reload in place (no scroll-to-bottom): editing an older note should not jump the list.
    this.applyFetchedNotes(await this.volateqApi.getAnalysisNotes(this.analysis.id));
  }

  private resetComposer() {
    this.editingNoteId = null;
    this.editingExistingAttachments = [];
    this.editingInitialAttachmentIds = [];
    this.replyingToNoteId = null;
    this.note = null;
    this.mentions = [];
    this.attachmentFiles = null;
    this.selectedEbNum = null;
    this.onCloseMentions();
  }

  /** A user written note may be edited only by its own author. */
  canEditNote(note: AnalysisNoteSchema): boolean {
    return (
      note.action_type === "NOTE" &&
      note.user !== null &&
      this.currentUserId !== null &&
      note.user.id === this.currentUserId
    );
  }

  parentNoteFor(note: AnalysisNoteSchema): AnalysisNoteSchema | null {
    return AnalysisNotesHelper.findNoteById(this.notes, note.parent_note_id);
  }

  hasResolvableParent(note: AnalysisNoteSchema): boolean {
    return this.parentNoteFor(note) !== null;
  }

  parentAuthorName(note: AnalysisNoteSchema): string {
    const parent = this.parentNoteFor(note);
    return parent ? this.authorName(parent) : "";
  }

  parentReplyPreview(note: AnalysisNoteSchema): string {
    const parent = this.parentNoteFor(note);
    return parent ? this.replyPreview(parent) : "";
  }

  replyPreview(note: AnalysisNoteSchema): string {
    const preview = AnalysisNotesHelper.replyPreviewText(note.description);
    return preview || this.$t("note-reply-preview-empty").toString();
  }

  @CatchError()
  onScrollToParent(note: AnalysisNoteSchema) {
    const parent = this.parentNoteFor(note);
    if (parent) {
      void this.onScrollToNote(parent.id);
    }
  }

  @CatchError()
  async onReplyToNote(note: AnalysisNoteSchema) {
    // Reply mode reuses the composer; cancel any in-progress edit first.
    this.editingNoteId = null;
    this.editingExistingAttachments = [];
    this.editingInitialAttachmentIds = [];
    this.attachmentFiles = null;
    this.replyingToNoteId = note.id;
    this.onCloseMentions();

    await this.$nextTick();
    this.noteTextarea?.focus();
  }

  @CatchError()
  onCancelReply() {
    this.replyingToNoteId = null;
  }

  @CatchError()
  async onScrollToNote(noteId: string) {
    const isVisible = this.filteredNotes.some(note => note.id === noteId);
    if (!isVisible) {
      // Parent may be hidden by the type/EB filter or the evaluation messages toggle; clear them so it can be scrolled to.
      let suppressCount = 0;
      if (this.selectedNoteTypes.length > 0) {
        suppressCount += 1;
      }
      if (this.selectedEbNums.length > 0) {
        suppressCount += 1;
      }
      this.filterScrollSuppressCount = suppressCount;
      this.selectedNoteTypes = [];
      this.selectedEbNums = [];
      this.showEvaluationMessages = true;
      await this.$nextTick();
    }

    const noteElement = this.notesList?.querySelector<HTMLElement>(`[data-note-id="${noteId}"]`);
    if (!noteElement) {
      return;
    }

    this.scrollToBottomCleanup?.();
    this.scrollToBottomCleanup = null;

    noteElement.scrollIntoView({ behavior: "smooth", block: "center" });
    this.clearHighlightTimeout();
    this.highlightedNoteId = noteId;
    this.highlightTimeout = window.setTimeout(() => {
      this.highlightedNoteId = null;
      this.highlightTimeout = null;
    }, 1600);
  }

  private clearHighlightTimeout() {
    if (this.highlightTimeout !== null) {
      window.clearTimeout(this.highlightTimeout);
      this.highlightTimeout = null;
    }
    this.highlightedNoteId = null;
  }

  @CatchError()
  async onEditNote(note: AnalysisNoteSchema) {
    // Editing reuses the composer (and its @-mention autocomplete): load the note's text in,
    // and switch the send button to "save". Reply mode is mutually exclusive with edit.
    this.replyingToNoteId = null;
    this.editingNoteId = note.id;
    this.note = note.description;
    this.mentions = [];
    this.attachmentFiles = null;
    this.editingExistingAttachments = [...note.attachments];
    this.editingInitialAttachmentIds = note.attachments.map(attachment => attachment.id);
    this.selectedEbNum = note.eval_batch_num;
    this.onCloseMentions();

    await this.$nextTick();
    this.noteTextarea?.focus();
  }

  @CatchError()
  onCancelNoteEdit() {
    this.resetComposer();
  }

  @CatchError("loading")
  async onDeleteNote(note: AnalysisNoteSchema) {
    if (!confirm(this.$t("sure-delete-something", { something: this.$t("note") }).toString())) {
      return;
    }

    await this.volateqApi.deleteAnalysisNote(this.analysis.id, note.id);

    if (this.editingNoteId === note.id || this.replyingToNoteId === note.id) {
      this.resetComposer();
    }

    this.applyFetchedNotes(await this.volateqApi.getAnalysisNotes(this.analysis.id));
    analysisEventService.emit(this.analysis.id, AnalysisEvent.UPDATE_ANALYSIS);
  }

  authorName(note: AnalysisNoteSchema): string {
    return note.user ? AnalysisNotesHelper.fullName(note.user) : this.$t("system").toString();
  }

  fullName(user: SimpleUserRoleSchema): string {
    return AnalysisNotesHelper.fullName(user);
  }

  renderBody(note: AnalysisNoteSchema): string {
    return AnalysisNotesHelper.renderDescriptionHtml(
      note.description,
      note.mentioned_users,
      this.currentUserId,
      note.action_type === "NOTE",
    );
  }

  groupedReactions(note: AnalysisNoteSchema): GroupedReaction[] {
    return AnalysisNotesHelper.groupReactions(note.reactions, this.currentUserId);
  }

  @CatchError()
  onToggleReactionPicker(noteId: string) {
    const wasOpen = this.openReactionPickerNoteId === noteId;
    this.closeReactionPicker();
    if (!wasOpen) {
      this.openReactionPickerNoteId = noteId;
      setTimeout(() => document.addEventListener("click", this.onDocumentClickForReactionPicker), 0);
    }
  }

  @CatchError()
  async onSelectReaction(note: AnalysisNoteSchema, emoji: string) {
    this.closeReactionPicker();
    await this.applyReaction(note, emoji);
  }

  @CatchError()
  async onToggleReaction(note: AnalysisNoteSchema, emoji: string) {
    await this.applyReaction(note, emoji);
  }

  private closeReactionPicker() {
    this.openReactionPickerNoteId = null;
    document.removeEventListener("click", this.onDocumentClickForReactionPicker);
  }

  private onDocumentClickForReactionPicker = () => {
    this.closeReactionPicker();
  };

  private async applyReaction(note: AnalysisNoteSchema, emoji: string) {
    const reactedByMe = this.groupedReactions(note).some(
      reaction => reaction.emoji === emoji && reaction.reactedByMe,
    );

    if (reactedByMe) {
      await this.volateqApi.removeAnalysisNoteReaction(this.analysis.id, note.id, emoji);
    } else {
      await this.volateqApi.addAnalysisNoteReaction(this.analysis.id, note.id, emoji);
    }

    // Reload the thread so the reaction counts reflect everyone's reactions, but don't
    // scroll: reacting to an older note should leave the user's scroll position untouched.
    this.applyFetchedNotes(await this.volateqApi.getAnalysisNotes(this.analysis.id));
  }

  isImage(attachment: AnalysisNoteAttachmentSchema): boolean {
    return AnalysisNotesHelper.isImageAttachment(attachment);
  }

  formatDateTime(isoDate: string): string {
    return AnalysisNotesHelper.formatDateTime(isoDate);
  }

  triggerFileInput() {
    this.fileInput?.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const selectedFiles = Array.from(input.files);
      this.attachmentFiles = [...this.attachmentFilesArray, ...selectedFiles];
    }
    input.value = "";
  }

  removeAttachedFile(index: number) {
    const files = [...this.attachmentFilesArray];
    files.splice(index, 1);
    this.attachmentFiles = files.length > 0 ? files : null;
  }

  removeEditingExistingAttachment(attachmentId: string) {
    this.editingExistingAttachments = this.editingExistingAttachments.filter(
      attachment => attachment.id !== attachmentId,
    );
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith("image/");
  }

  private async loadVolateqUsers() {
    try {
      this.volateqUsers = await this.volateqApi.getVolateqUsers();
    } catch {
      // Mentions are optional; users without READ_VOLATEQ_USER simply can't tag anyone.
      this.volateqUsers = [];
    }
  }

  private async updateNotes(forceScrollToBottom = false) {
    const fetchedNotes = await this.volateqApi.getAnalysisNotes(this.analysis.id);
    // A note arriving in the background must not pull a user who scrolled up back down.
    const wasScrolledToBottom = !this.notesList || DomHelper.isScrolledToBottom(this.notesList);

    this.applyFetchedNotes(fetchedNotes);
    // Skip while the initial skeleton is up — created() scrolls once notes are visible.
    if (this.notesLoading) {
      return;
    }
    if (!forceScrollToBottom && !wasScrolledToBottom) {
      return;
    }
    await this.$nextTick();
    this.scrollNotesListToBottom();
  }

  /**
   * Renders a freshly fetched thread and reports whether it actually differed from the rendered
   * one. Fetching alone changes the presigned attachment urls, so re-rendering an unchanged
   * thread would make the browser download and repaint every image again for nothing.
   */
  private applyFetchedNotes(fetchedNotes: AnalysisNoteSchema[]): boolean {
    const fingerprint = AnalysisNotesHelper.threadFingerprint(fetchedNotes);
    if (fingerprint === this.renderedThreadFingerprint) {
      return false;
    }

    this.renderedThreadFingerprint = fingerprint;
    this.notes = AnalysisNotesHelper.carryOverAttachmentUrls(this.notes, fetchedNotes);
    this.clearStaleReplyTarget();

    return true;
  }

  private clearStaleReplyTarget() {
    if (this.replyingToNoteId && !AnalysisNotesHelper.findNoteById(this.notes, this.replyingToNoteId)) {
      this.replyingToNoteId = null;
    }
  }

  private scrollNotesListToBottom() {
    this.scrollToBottomCleanup?.();
    this.scrollToBottomCleanup = null;

    if (this.notesList) {
      this.scrollToBottomCleanup = DomHelper.scrollToBottomWhenContentSettled(this.notesList);
    }
  }
}
</script>

<style lang="scss">
@import "@/scss/_colors.scss";

.app-analysis-notes-and-history {
  &--embedded {
    margin-top: 0;
  }

  &-tabs {
    display: flex;
    gap: 0.25rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    margin-bottom: 0.75rem;
  }

  &-tab {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 0.4rem 0.75rem;
    cursor: pointer;
    color: inherit;
    opacity: 0.6;

    &.is-active {
      opacity: 1;
      border-bottom-color: var(--primary-bg-color);
      font-weight: 600;
    }
  }

  &-filter-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-bottom: 0.5em;

    &.has-toolbar {
      justify-content: space-between;
    }
  }

  &-toolbar {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    min-width: 0;
  }

  &-type-filter {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 0 1 auto;
    min-width: 0;
    gap: 0.5em;

    .notes-subscription-btn,
    .notes-evaluation-messages-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      padding: 0;
      background: none;
      border: 1px solid var(--default-border-color);
      color: var(--muted-text-color);
      cursor: pointer;
      transition: color 0.2s, border-color 0.2s, background-color 0.2s;

      &:hover:not(:disabled) {
        color: var(--primary-bg-color);
        border-color: var(--primary-border-color);
      }

      &.is-subscribed,
      &.is-active {
        color: var(--primary-bg-color);
        border-color: var(--primary-border-color);
        background-color: var(--secondary-bg-highlight-color);
      }

      &.is-mandatory:disabled {
        cursor: not-allowed;
        opacity: 0.85;
      }

      &:focus {
        outline: none;
      }
    }

    .app-multiselect {
      width: 14rem;
      max-width: 50%;

      &:only-child {
        max-width: none;
      }
    }
  }

  @media (max-width: 991px) {
    &-filter-row.has-toolbar {
      flex-direction: column;
      align-items: stretch;
    }

    &-toolbar {
      flex: none;
      margin-right: 0;
    }

    &-type-filter {
      width: 100%;
    }
  }

  &-list {
    max-height: 600px;
    padding: 0.2em 0.4em;
    background-color: transparent;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.5em; // compact gap between cards

    // Custom modern scrollbar
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: var(--default-border-2-color);
    }

    .note-entry {
      background-color: var(--default-bg-color);
      border: 1px solid var(--default-border-color);
      padding: 0.5em 0.8em;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
      border-radius: 0;
      transition: box-shadow 0.2s;
      display: flex;
      flex-direction: column;
      gap: 0.3em;
      min-width: 0;

      &:hover {
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
      }

      &.note-entry--highlight {
        box-shadow: 0 0 0 2px var(--primary-bg-color);
      }

      &.note-entry--skeleton {
        pointer-events: none;
        box-shadow: none;
        min-height: 2.75rem;
        justify-content: center;

        &:hover {
          box-shadow: none;
        }

        &.note-entry-note {
          border-left: 3px solid var(--primary-bg-color);
          min-height: 3.5rem;
        }

        &.note-entry--history {
          border-left: 2px solid var(--default-border-2-color);
          min-height: 2.25rem;
        }

        .note-skeleton-bar {
          display: block;
          height: 0.9em;
          background-color: var(--default-border-2-color);
          opacity: 0.55;
          animation: note-skeleton-pulse 1.4s ease-in-out infinite;
        }

        &:nth-child(2) .note-skeleton-bar {
          animation-delay: 0.1s;
        }

        &:nth-child(3) .note-skeleton-bar {
          animation-delay: 0.2s;
        }

        &:nth-child(4) .note-skeleton-bar {
          animation-delay: 0.3s;
        }

        &:nth-child(5) .note-skeleton-bar {
          animation-delay: 0.4s;
        }
      }

      &.note-entry-note {
        border-left: 3px solid var(--primary-bg-color);
        padding: 0.65em 0.9em;
        gap: 0.4em;

        .note-entry-author {
          font-size: 0.9rem;
        }

        .note-entry-body {
          font-size: 0.9rem;
        }
      }

      &.note-entry--history {
        background-color: transparent;
        border-color: var(--default-border-2-color);
        box-shadow: none;
        padding: 0.35em 0.65em;
        gap: 0.15em;

        &:hover {
          box-shadow: none;
        }

        .note-entry-author {
          font-weight: 500;
          font-size: 0.78rem;
          color: var(--muted-text-color);
        }

        .note-entry-type-label {
          font-size: 0.62rem;
          font-weight: 600;
          opacity: 0.75;
        }

        .note-entry-time {
          font-size: 0.68rem;
        }

        .note-entry-body {
          font-size: 0.78rem;
          line-height: 1.35;
          color: var(--muted-text-color);
        }
      }

      // Left border color-coding by action type (history entries use thinner, softer accents)
      &.note-entry-state_update {
        border-left: 3px solid #10b981;
        &.note-entry--history { border-left: 2px solid rgba(16, 185, 129, 0.45); }
      }
      &.note-entry-release_lock {
        border-left: 3px solid #6b7280;
        &.note-entry--history { border-left: 2px solid rgba(107, 114, 128, 0.4); }
      }
      &.note-entry-changed_flown_at {
        border-left: 3px solid #8b5cf6;
        &.note-entry--history { border-left: 2px solid rgba(139, 92, 246, 0.45); }
      }
      &.note-entry-task {
        border-left: 3px solid #f59e0b;
        &.note-entry--history { border-left: 2px solid rgba(245, 158, 11, 0.45); }
      }
      &.note-entry-role_assigned {
        border-left: 3px solid #06b6d4;
        &.note-entry--history { border-left: 2px solid rgba(6, 182, 212, 0.45); }
      }
      &.note-entry-ready_for_review {
        border-left: 3px solid #3b82f6;
        &.note-entry--history { border-left: 2px solid rgba(59, 130, 246, 0.45); }
      }
      // Same red/yellow as the Ops user's notification of the message.
      &.note-entry-evaluation_error.note-entry--history {
        border-left: 3px solid var(--danger-bg-color);
      }
      &.note-entry-evaluation_info.note-entry--history {
        border-left: 3px solid var(--warning-bg-color);
      }

      &-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.8em;
        flex-wrap: wrap;
        border-bottom: none; // removed border line to save space
        padding-bottom: 0;
        margin-bottom: 0;
      }

      &-author {
        font-weight: 600;
        font-size: 0.85rem;
        color: var(--default-text-color);

        &.is-system {
          color: var(--muted-text-color);
          font-weight: 500;
          font-style: italic;
        }
      }

      &-meta {
        display: flex;
        align-items: center;
        gap: 0.8em;
        margin-left: auto;
      }

      .note-entry-eb-badge {
        display: inline-flex;
        align-items: center;
        font-size: 0.62rem;
        font-weight: 700;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        padding: 0.05em 0.4em;
        color: var(--primary-bg-color);
        border: 1px solid var(--primary-border-color);
        white-space: nowrap;
      }

      &-type-label {
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        display: inline-flex;
        align-items: center;
        gap: 0.3em;

        &::before {
          content: "";
          display: inline-block;
          width: 5px;
          height: 5px;
        }

        &.type-note {
          color: var(--primary-bg-color);
          &::before { background-color: var(--primary-bg-color); }
        }
        &.type-state_update {
          color: #10b981;
          &::before { background-color: #10b981; }
        }
        &.type-release_lock {
          color: #6b7280;
          &::before { background-color: #6b7280; }
        }
        &.type-changed_flown_at {
          color: #8b5cf6;
          &::before { background-color: #8b5cf6; }
        }
        &.type-task {
          color: #f59e0b;
          &::before { background-color: #f59e0b; }
        }
        &.type-role_assigned {
          color: #06b6d4;
          &::before { background-color: #06b6d4; }
        }
        &.type-ready_for_review {
          color: #3b82f6;
          &::before { background-color: #3b82f6; }
        }
        &.type-deadline_changed {
          color: #d97706;
          &::before { background-color: #d97706; }
        }
        &.type-changed_product_packages {
          color: #ec4899;
          &::before { background-color: #ec4899; }
        }
        &.type-changed_drone {
          color: #84cc16;
          &::before { background-color: #84cc16; }
        }
        &.type-changed_key_figures {
          color: #14b8a6;
          &::before { background-color: #14b8a6; }
        }
        &.type-evaluation_error {
          color: var(--danger-bg-color);
          &::before { background-color: var(--danger-bg-color); }
        }
        &.type-evaluation_info {
          color: var(--warning-bg-color);
          &::before { background-color: var(--warning-bg-color); }
        }
      }

      &-time {
        font-size: 0.75rem;
        color: var(--muted-text-color);
        white-space: nowrap;
      }

      &-body {
        font-size: 0.85rem;
        line-height: 1.4;
        color: var(--default-text-color);
        word-break: break-word;
      }

      &-edited {
        font-size: 0.7rem;
        font-style: italic;
        color: var(--muted-text-color);
      }

      &-reply-btn,
      &-edit-btn,
      &-delete-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        background: none;
        border: none;
        color: var(--muted-text-color);
        cursor: pointer;
        transition: color 0.2s;

        &:focus {
          outline: none;
        }
      }

      &-reply-btn,
      &-edit-btn {
        &:hover,
        &.is-active {
          color: var(--primary-bg-color);
        }
      }

      &-delete-btn {
        &:hover {
          color: var(--danger-text-color);
        }
      }

      &-reply-quote {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.1em;
        width: 100%;
        margin: 0;
        padding: 0.35em 0.55em;
        background-color: var(--default-bg-hover-color);
        border: none;
        border-left: 3px solid var(--primary-border-color);
        text-align: left;
        cursor: pointer;
        transition: background-color 0.15s;

        &:hover {
          background-color: var(--secondary-bg-highlight-color);
        }

        &:focus {
          outline: none;
        }
      }

      &-reply-quote-author {
        font-size: 0.72rem;
        font-weight: 600;
        color: var(--primary-bg-color);
      }

      &-reply-quote-text {
        font-size: 0.75rem;
        color: var(--muted-text-color);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }

      &-attachments {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4em;
        margin-top: 0.2em;
        min-width: 0;
        max-width: 100%;
      }

      &-attachment-image {
        max-height: 60px;
        max-width: 90px;
        border: 1px solid var(--default-border-color);
        border-radius: 0; // strictly no rounded corners
        transition: opacity 0.2s;

        &:hover {
          opacity: 0.85;
        }
      }

      &-attachment {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        background-color: var(--default-bg-hover-color);
        border: 1px solid var(--default-border-color);
        padding: 0.25em 0.5em;
        color: var(--primary-bg-color);
        font-size: 0.75rem;
        font-weight: 500;
        text-decoration: none;
        border-radius: 0; // strictly no rounded corners
        transition: background-color 0.2s, border-color 0.2s;

        &:hover {
          background-color: var(--secondary-bg-highlight-color);
          border-color: var(--primary-border-color);
          text-decoration: none;
        }

        &-file {
          display: flex;
          align-items: center;
          gap: 0.4em;
          min-width: 0;
          max-width: 100%;
        }

        &-filename {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }
      }
    }

    .note-entry-reactions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.3em;
      margin-top: 0.1em;
    }

    .note-reaction-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.25em;
      padding: 0.05em 0.45em;
      background-color: var(--default-bg-hover-color);
      border: 1px solid var(--default-border-color);
      color: var(--default-text-color);
      font-size: 0.78rem;
      line-height: 1.6;
      cursor: pointer;
      transition: background-color 0.2s, border-color 0.2s;

      &:hover {
        border-color: var(--primary-border-color);
      }

      &.is-mine {
        background-color: var(--secondary-bg-highlight-color);
        border-color: var(--primary-border-color);
        color: var(--primary-bg-color);
      }

      &:focus {
        outline: none;
      }

      .note-reaction-count {
        font-weight: 600;
      }
    }

    .note-reaction-add {
      position: relative;
      display: inline-flex;
    }

    .note-reaction-add-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      background-color: transparent;
      border: 1px solid var(--default-border-color);
      color: var(--muted-text-color);
      cursor: pointer;
      transition: color 0.2s, border-color 0.2s;

      &:hover {
        color: var(--primary-bg-color);
        border-color: var(--primary-border-color);
      }

      &:focus {
        outline: none;
      }
    }

    .note-reaction-picker {
      position: absolute;
      z-index: 20;
      bottom: 100%;
      left: 0;
      margin-bottom: 0.25em;
      display: flex;
      gap: 0.15em;
      padding: 0.25em;
      background-color: var(--default-bg-color);
      border: 1px solid var(--default-border-color);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }

    .note-reaction-picker-option {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.15em 0.3em;
      background-color: transparent;
      border: none;
      font-size: 1rem;
      line-height: 1;
      cursor: pointer;
      transition: transform 0.1s;

      &:hover {
        transform: scale(1.25);
      }

      &:focus {
        outline: none;
      }
    }

    .app-note-mention {
      font-weight: 500;
      color: var(--muted-text-color);
    }

    .app-note-mention-self {
      font-weight: 700;
      color: #ea580c;
      background-color: rgba(234, 88, 12, 0.14);
      padding: 0 0.2em;
    }

    .app-note-link {
      color: #0066cc;
      text-decoration: none;

      &:hover {
        color: #0066cc;
        text-decoration: underline;
      }
    }
  }

  &-composer {
    position: relative;
    margin-top: 0.8em;

    .composer-editing-banner,
    .composer-replying-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5em;
      margin-bottom: 0.4em;
      padding: 0.3em 0.6em;
      background-color: var(--secondary-bg-highlight-color);
      border-left: 3px solid var(--primary-bg-color);
      font-size: 0.8rem;
      color: var(--default-text-color);
    }

    .composer-replying-preview {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.1em;
      min-width: 0;
      flex: 1;
      background: none;
      border: none;
      padding: 0;
      text-align: left;
      color: inherit;
      cursor: pointer;

      &:hover .composer-replying-preview-text {
        text-decoration: underline;
      }

      &:focus {
        outline: none;
      }
    }

    .composer-replying-preview-text {
      font-size: 0.72rem;
      color: var(--muted-text-color);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }

    .composer-editing-cancel {
      background: none;
      border: none;
      padding: 0;
      color: var(--primary-bg-color);
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      flex-shrink: 0;

      &:hover {
        text-decoration: underline;
      }

      &:focus {
        outline: none;
      }
    }

    textarea {
      border-radius: 0 !important;
      border: 1px solid var(--default-border-color);
      padding: 0.6em;
      font-size: 0.85rem;
      transition: border-color 0.2s, box-shadow 0.2s;
      resize: vertical;

      &:focus {
        border-color: var(--primary-bg-color);
        box-shadow: none;
      }
    }

    .composer-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 0.5em;
      gap: 1em;
    }

    .composer-actions-left {
      display: flex;
      align-items: center;
      gap: 0.5em;
    }

    .composer-actions-right {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.2em;
    }

    .composer-send-hint {
      font-size: 0.7rem;
      line-height: 1.2;
    }

    .btn-attach {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      background-color: var(--default-bg-hover-color);
      border: 1px solid var(--default-border-color);
      color: var(--default-text-color);
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background-color: var(--secondary-bg-highlight-color);
        border-color: var(--primary-border-color);
        color: var(--primary-bg-color);
      }

      &:focus {
        outline: none;
      }
    }
  }

  .attached-files-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4em;
    margin-top: 0.5em;
    padding: 0.4em;
    background-color: var(--default-bg-hover-color);
    border: 1px solid var(--default-border-color);
  }

  .attached-file-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    background-color: var(--default-bg-color);
    border: 1px solid var(--default-border-color);
    padding: 0.2em 0.5em;
    font-size: 0.75rem;
    color: var(--default-text-color);

    .file-name {
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .btn-remove-file {
      background: none;
      border: none;
      padding: 0;
      margin-left: 0.2em;
      color: var(--danger-text-color);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      opacity: 0.7;
      transition: opacity 0.2s;

      &:hover {
        opacity: 1;
      }

      &:focus {
        outline: none;
      }
    }
  }

  &-mentions {
    position: absolute;
    z-index: 10;
    width: 100%;
    max-height: 180px;
    overflow-y: auto;
    border: 1px solid var(--default-border-color);
    background-color: var(--default-bg-color);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    border-radius: 0;

    .mention-suggestion {
      padding: 0.5em 0.8em;
      cursor: pointer;
      border-bottom: 1px solid var(--default-border-color);
      font-size: 0.85rem;

      &:last-child {
        border-bottom: none;
      }

      &.is-active,
      &:hover {
        background-color: var(--secondary-bg-highlight-color);
        color: var(--primary-bg-color);
      }
    }
  }
}

@keyframes note-skeleton-pulse {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 0.75;
  }
}
</style>
